/**
 * Classic Sidebar - Contextual Navigation
 * 
 * Shows section-specific content based on active route
 * Replicates original MARS2 document tree structure
 */

import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  ChevronRight,
  ChevronDown,
  FileText,
  Folder,
  Wrench,
  Users,
  Shield,
  Package,
  FileBarChart,
  Award,
  Settings,
} from 'lucide-react';

export default function ClassicSidebar() {
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState<string[]>(['1', '2', '3']);

  const toggleSection = (id: string) => {
    setExpandedSections(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  // Determine which section we're in
  const currentSection = location.pathname.split('/')[1] || 'dashboard';

  // Dashboard sidebar content
  const dashboardContent = {
    title: 'Quick Access',
    items: [
      { id: '1', label: 'Overview', path: '/', icon: FileText },
      { id: '2', label: 'Recent Activity', path: '/', icon: FileBarChart },
      { id: '3', label: 'Alerts', path: '/', icon: Shield },
    ],
  };

  // Certificates sidebar content
  const certificatesContent = {
    title: 'Certificates & Documents',
    sections: [
      {
        id: '1',
        label: 'Ship Certificates',
        icon: Award,
        items: [
          { path: '/certificates', label: 'All Certificates' },
          { path: '/certificates/expiring', label: 'Expiring Soon' },
          { path: '/certificates', label: 'Class Certificates' },
          { path: '/certificates', label: 'Safety Certificates' },
        ],
      },
      {
        id: '2',
        label: 'Statutory Documents',
        icon: FileText,
        items: [
          { path: '/certificates', label: 'SOLAS Documents' },
          { path: '/certificates', label: 'MARPOL Documents' },
          { path: '/certificates', label: 'Load Line Certificate' },
        ],
      },
      {
        id: '3',
        label: 'ISM Documents',
        icon: Folder,
        items: [
          { path: '/certificates', label: 'DOC (Document of Compliance)' },
          { path: '/certificates', label: 'SMC (Safety Management Certificate)' },
          { path: '/certificates', label: 'Audit Reports' },
        ],
      },
    ],
  };

  // Maintenance sidebar content
  const maintenanceContent = {
    title: 'Maintenance Management',
    sections: [
      {
        id: '1',
        label: 'Equipment',
        icon: Wrench,
        items: [
          { path: '/maintenance/equipment', label: 'All Equipment' },
          { path: '/maintenance/equipment', label: 'Main Engine' },
          { path: '/maintenance/equipment', label: 'Auxiliary Engines' },
          { path: '/maintenance/equipment', label: 'Deck Machinery' },
          { path: '/maintenance/equipment', label: 'Navigation Equipment' },
        ],
      },
      {
        id: '2',
        label: 'Work Orders',
        icon: FileText,
        items: [
          { path: '/maintenance/work-orders', label: 'Active Work Orders' },
          { path: '/maintenance/work-orders', label: 'Planned Maintenance' },
          { path: '/maintenance/work-orders', label: 'Corrective Maintenance' },
          { path: '/maintenance/work-orders', label: 'Completed Jobs' },
        ],
      },
      {
        id: '3',
        label: 'Schedule',
        icon: FileBarChart,
        items: [
          { path: '/maintenance/calendar', label: 'Maintenance Calendar' },
          { path: '/maintenance/calendar', label: 'Due This Week' },
          { path: '/maintenance/calendar', label: 'Overdue Items' },
        ],
      },
    ],
  };

  // Crew sidebar content
  const crewContent = {
    title: 'Crew Management',
    sections: [
      {
        id: '1',
        label: 'Personnel',
        icon: Users,
        items: [
          { path: '/crew/personnel', label: 'All Crew Members' },
          { path: '/crew/personnel', label: 'Officers' },
          { path: '/crew/personnel', label: 'Ratings' },
          { path: '/crew/personnel', label: 'Shore Staff' },
        ],
      },
      {
        id: '2',
        label: 'Crew Certificates',
        icon: Award,
        items: [
          { path: '/crew/certificates', label: 'All Certificates' },
          { path: '/crew/certificates', label: 'COC (Certificate of Competency)' },
          { path: '/crew/certificates', label: 'STCW Certificates' },
          { path: '/crew/certificates', label: 'Medical Certificates' },
          { path: '/crew/certificates', label: 'Expiring Soon' },
        ],
      },
      {
        id: '3',
        label: 'Training & Hours',
        icon: FileText,
        items: [
          { path: '/crew/training', label: 'Training Records' },
          { path: '/crew/hours', label: 'Working Hours' },
          { path: '/crew/hours', label: 'Rest Hours Compliance' },
        ],
      },
    ],
  };

  // Safety sidebar content
  const safetyContent = {
    title: 'Safety Management',
    sections: [
      {
        id: '1',
        label: 'ISM Checklists & Forms',
        icon: FileText,
        items: [
          { path: '/safety/inspections', label: 'Safety Inspections' },
          { path: '/safety/drills', label: 'Emergency Drills' },
          { path: '/safety/risk-assessments', label: 'Risk Assessments' },
          { path: '/safety/inspections', label: 'Permit to Work' },
        ],
      },
      {
        id: '2',
        label: 'ISPS Forms',
        icon: Shield,
        items: [
          { path: '/safety/inspections', label: 'Security Assessments' },
          { path: '/safety/inspections', label: 'Security Drills' },
          { path: '/safety/inspections', label: 'Access Control' },
        ],
      },
      {
        id: '3',
        label: 'Incidents & Reports',
        icon: FileText,
        items: [
          { path: '/safety/incidents', label: 'All Incidents' },
          { path: '/safety/incidents', label: 'Near Misses' },
          { path: '/safety/incidents', label: 'Accidents' },
          { path: '/safety/incidents', label: 'Investigation Reports' },
        ],
      },
    ],
  };

  // Inventory sidebar content
  const inventoryContent = {
    title: 'Inventory Management',
    sections: [
      {
        id: '1',
        label: 'Stock Items',
        icon: Package,
        items: [
          { path: '/inventory', label: 'All Items' },
          { path: '/inventory', label: 'Spare Parts' },
          { path: '/inventory', label: 'Consumables' },
          { path: '/inventory', label: 'Low Stock Items' },
        ],
      },
      {
        id: '2',
        label: 'Transactions',
        icon: FileBarChart,
        items: [
          { path: '/inventory/transactions', label: 'All Transactions' },
          { path: '/inventory/transactions', label: 'Issues' },
          { path: '/inventory/transactions', label: 'Receipts' },
          { path: '/inventory/transactions', label: 'Transfers' },
        ],
      },
      {
        id: '3',
        label: 'Requisitions',
        icon: FileText,
        items: [
          { path: '/inventory/requisitions', label: 'All Requisitions' },
          { path: '/inventory/requisitions', label: 'Pending Approval' },
          { path: '/inventory/requisitions', label: 'Approved' },
          { path: '/inventory/requisitions', label: 'Ordered' },
        ],
      },
    ],
  };

  // Documents sidebar content
  const documentsContent = {
    title: 'Document Management',
    sections: [
      {
        id: '1',
        label: 'Company Forms',
        icon: FileText,
        items: [
          { path: '/documents', label: 'All Documents' },
          { path: '/documents', label: 'Company Procedures' },
          { path: '/documents', label: 'Work Instructions' },
          { path: '/documents', label: 'Checklists' },
        ],
      },
      {
        id: '2',
        label: 'File Folder Organisation',
        icon: Folder,
        items: [
          { path: '/documents', label: 'Technical Manuals' },
          { path: '/documents', label: 'Operating Procedures' },
          { path: '/documents', label: 'Safety Manuals' },
        ],
      },
      {
        id: '3',
        label: 'MMP Appendices',
        icon: FileText,
        items: [
          { path: '/documents', label: 'Master Manual Appendices' },
          { path: '/documents', label: 'Record Books' },
          { path: '/documents', label: 'Log Books' },
        ],
      },
    ],
  };

  // Get content based on current section
  const getContent = () => {
    switch (currentSection) {
      case 'certificates':
        return certificatesContent;
      case 'maintenance':
        return maintenanceContent;
      case 'crew':
        return crewContent;
      case 'safety':
        return safetyContent;
      case 'inventory':
        return inventoryContent;
      case 'documents':
        return documentsContent;
      default:
        return dashboardContent;
    }
  };

  const content = getContent();

  // Render simple list (for dashboard)
  if ('items' in content) {
    return (
      <div className="classic-tree-view flex h-full flex-col">
        <div className="p-3 border-b border-gray-300 bg-gray-100">
          <div className="font-bold text-xs uppercase text-gray-700">{content.title}</div>
        </div>
        <nav className="flex-1 overflow-y-auto p-2">
          {content.items.map((item) => (
            <NavLink
              key={item.id}
              to={item.path}
              className={({ isActive }) =>
                `classic-tree-item ${isActive ? 'selected' : ''}`
              }
            >
              <item.icon className="h-4 w-4 flex-shrink-0" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* System Navigation Footer */}
        <div className="border-t border-gray-300 bg-gray-50">
          <div className="p-2">
            <div className="text-xs font-bold uppercase text-gray-600 px-3 py-2">System</div>
            <NavLink
              to="/reports"
              className={({ isActive }) =>
                `classic-tree-item ${isActive ? 'selected' : ''}`
              }
            >
              <FileBarChart className="h-4 w-4 flex-shrink-0" />
              <span>Reports</span>
            </NavLink>
            <NavLink
              to="/users"
              className={({ isActive }) =>
                `classic-tree-item ${isActive ? 'selected' : ''}`
              }
            >
              <Users className="h-4 w-4 flex-shrink-0" />
              <span>Users</span>
            </NavLink>
            <NavLink
              to="/settings"
              className={({ isActive }) =>
                `classic-tree-item ${isActive ? 'selected' : ''}`
              }
            >
              <Settings className="h-4 w-4 flex-shrink-0" />
              <span>Settings</span>
            </NavLink>
          </div>
        </div>
      </div>
    );
  }

  // Render tree structure (for other sections)
  return (
    <div className="classic-tree-view flex h-full flex-col">
      <div className="p-3 border-b border-gray-300 bg-gray-100">
        <div className="font-bold text-xs uppercase text-gray-700">{content.title}</div>
      </div>
      <nav className="flex-1 overflow-y-auto p-2">
        {content.sections.map((section, idx) => {
          const isExpanded = expandedSections.includes(section.id);
          const SectionIcon = section.icon;

          return (
            <div key={section.id} className="mb-1">
              {/* Section Header */}
              <div
                onClick={() => toggleSection(section.id)}
                className="classic-tree-item font-bold cursor-pointer"
              >
                {isExpanded ? (
                  <ChevronDown className="h-3 w-3 tree-icon" />
                ) : (
                  <ChevronRight className="h-3 w-3 tree-icon" />
                )}
                <SectionIcon className="h-4 w-4" />
                <span>{idx + 1}. {section.label}</span>
              </div>

              {/* Section Items */}
              {isExpanded && (
                <div className="classic-tree-group">
                  {section.items.map((item, itemIdx) => (
                    <NavLink
                      key={`${section.id}-${itemIdx}`}
                      to={item.path}
                      className={({ isActive }) =>
                        `classic-tree-item ${isActive ? 'selected' : ''}`
                      }
                    >
                      <FileText className="h-3.5 w-3.5 flex-shrink-0 opacity-60" />
                      <span>{item.label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* System Navigation Footer */}
      <div className="border-t border-gray-300 bg-gray-50">
        <div className="p-2">
          <div className="text-xs font-bold uppercase text-gray-600 px-3 py-2">System</div>
          <NavLink
            to="/reports"
            className={({ isActive }) =>
              `classic-tree-item ${isActive ? 'selected' : ''}`
            }
          >
            <FileBarChart className="h-4 w-4 flex-shrink-0" />
            <span>Reports</span>
          </NavLink>
          <NavLink
            to="/users"
            className={({ isActive }) =>
              `classic-tree-item ${isActive ? 'selected' : ''}`
            }
          >
            <Users className="h-4 w-4 flex-shrink-0" />
            <span>Users</span>
          </NavLink>
          <NavLink
            to="/settings"
            className={({ isActive }) =>
              `classic-tree-item ${isActive ? 'selected' : ''}`
            }
          >
            <Settings className="h-4 w-4 flex-shrink-0" />
            <span>Settings</span>
          </NavLink>
        </div>
      </div>
    </div>
  );
}


